import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { NgMapsCoreModule } from '@ng-maps/core';
import { NgMapsGoogleModule } from '@ng-maps/google';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { MultiStatChartComponent, SiteTableComponent } from '@components';
import { AccountDto, SiteDto, Timestamp } from '@dto';
import { AccountStatsService } from '@services';

@Component({
  imports: [MatIcon, TranslatePipe, NgMapsGoogleModule, NgMapsCoreModule, SiteTableComponent, MultiStatChartComponent],
  selector: 'monitor-ui-account',
  styleUrls: ['./account.component.scss'],
  templateUrl: './account.component.html',
})
export class AccountComponent implements OnInit, OnDestroy {
  account = new AccountDto();
  accountBessPower = 0.0;
  accountEnergyCapacity = 0.0;
  aggregatedBuildingDemandLabel = 'Building Demand';
  aggregatedBuildingDemandStats: Timestamp[];
  globalLatitude: number | undefined;
  globalLongitude: number | undefined;
  globalZoom: number | undefined;
  siteMeterLabel = 'Site Meter';
  siteMeterStats: Timestamp[];

  sites: SiteDto[] = [];
  private _onDestroy = new Subject<void>();

  constructor(
    private accountStatsService: AccountStatsService,
    private translate: TranslateService,
    private route: ActivatedRoute,
  ) {
    this.aggregatedBuildingDemandLabel = this.translate.instant('aggregated.building.demand.series');
    this.siteMeterLabel = this.translate.instant('aggregated.site.meter.series');

    // set chart default state to show full year of months on x-axis
    const now = new Date();
    const getTodayPlusNumberOfMonths = (numMonths: number): number => {
      const temp = new Date();
      // add a year if we are going past December
      if (temp.getMonth() + numMonths > 12) {
        temp.setFullYear(now.getFullYear() + 1);
      }
      temp.setMonth((now.getMonth() + numMonths) % 12);
      return Number(temp.toISOString().substring(0, now.toISOString().length - 5));
    };

    this.aggregatedBuildingDemandStats = [
      { timestamp: getTodayPlusNumberOfMonths(0), value: null },
      { timestamp: getTodayPlusNumberOfMonths(1), value: null },
      { timestamp: getTodayPlusNumberOfMonths(2), value: null },
      { timestamp: getTodayPlusNumberOfMonths(3), value: null },
      { timestamp: getTodayPlusNumberOfMonths(4), value: null },
      { timestamp: getTodayPlusNumberOfMonths(5), value: null },
      { timestamp: getTodayPlusNumberOfMonths(6), value: null },
      { timestamp: getTodayPlusNumberOfMonths(7), value: null },
      { timestamp: getTodayPlusNumberOfMonths(8), value: null },
      { timestamp: getTodayPlusNumberOfMonths(9), value: null },
      { timestamp: getTodayPlusNumberOfMonths(10), value: null },
      { timestamp: getTodayPlusNumberOfMonths(11), value: null },
    ];
    this.siteMeterStats = [...this.aggregatedBuildingDemandStats];
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
  }

  ngOnInit(): void {
    this.route.data.subscribe((data: { data: AccountDto }) => {
      this.getMapCenterAndZoom(data.data.children);
      this.account = data.data;

      this.accountStatsService
        .getAggregatedPowerCapacityForAccount$(this.account.id)
        .pipe(takeUntil(this._onDestroy))
        .subscribe(bessPower => (this.accountBessPower = bessPower));

      this.accountStatsService
        .getAggregatedEnergyCapacityForAccount$(this.account.id)
        .pipe(takeUntil(this._onDestroy))
        .subscribe(energyCapacity => (this.accountEnergyCapacity = energyCapacity));

      this.accountStatsService
        .getBuildingDemandForAccount$(this.account.id)
        .pipe(takeUntil(this._onDestroy))
        .subscribe(stats => (this.aggregatedBuildingDemandStats = stats));

      this.accountStatsService
        .getSiteMeterForAccount$(this.account.id)
        .pipe(takeUntil(this._onDestroy))
        .subscribe(stats => (this.siteMeterStats = stats));
    });
  }

  private getMapCenterAndZoom(sites: SiteDto[]): void {
    if (!sites?.length) return;

    const lats = sites.map(s => s.latitude);
    const lngs = sites.map(s => s.longitude);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    // Optional: Estimate zoom level based on bounds (simplified for example)
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    let zoom = 4;
    if (maxDiff < 0.1) zoom = 12;
    else if (maxDiff < 0.5) zoom = 10;
    else if (maxDiff < 1) zoom = 8;
    else if (maxDiff < 5) zoom = 6;
    else if (maxDiff < 10) zoom = 5;
    else zoom = 4;

    this.globalLatitude = centerLat;
    this.globalLongitude = centerLng;
    this.globalZoom = zoom;
  }
}
